import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Hash, Plus, Send, RefreshCw, History, X, MessageSquareOff } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";
import { scrollBoxToBottom } from "@/lib/utils";

export default function ClinicianTeam() {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);           // messages currently visible in the pane
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [text, setText] = useState("");
  const [newChan, setNewChan] = useState({ name: "", description: "" });
  const [showNew, setShowNew] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(true);
  // Per-channel unread counts driving the Slack-style activity dot in the
  // sidebar. Populated by `/api/team/unread` every 10s.
  const [unreadByChan, setUnreadByChan] = useState({});
  const bottom = useRef(null);
  const scrollBox = useRef(null);

  const refreshUnread = async () => {
    try {
      const { data } = await api.get("/team/unread");
      setUnreadByChan(data?.per_channel || {});
    } catch { /* transient — leave the previous badge state */ }
  };

  const refreshChans = async () => {
    const { data } = await api.get("/team/channels");
    setChannels(data);
    setChannelsLoading(false);
    // Keep the active channel in sync with the server payload — the fresh
    // row carries the authoritative `ended_at` (someone else may have ended
    // the chat while we were browsing).
    if (active) {
      const updated = data.find((c) => c.id === active.id);
      if (updated) setActive(updated);
    } else if (data.length) {
      setActive(data[0]);
    }
    return data;
  };
  useEffect(() => { refreshChans(); refreshUnread(); }, []);

  // The channel row itself carries the clinic-wide End Chat cutoff. When the
  // server value changes the visible pane switches to the "chat cleared" state.
  const endedAt = active?.ended_at || null;

  // Load messages for the active channel, honouring the "End chat" cutoff.
  // If the user ended the chat, we only load messages posted AFTER the cutoff
  // until they explicitly reveal history.
  const loadMessages = async (channelId, opts = {}) => {
    if (!channelId) return [];
    if (opts.spin) setRefreshing(true);
    try {
      const { data } = await api.get(`/team/messages/${channelId}`);
      const cutoff = opts.ignoreCutoff ? null : endedAt;
      const filtered = cutoff ? data.filter((m) => m.created_at > cutoff) : data;
      setMessages(filtered);
      if (opts.ignoreCutoff) setHistoryLoaded(true);
      return filtered;
    } finally {
      if (opts.spin) setRefreshing(false);
    }
  };

  // Channel switch → reload messages fresh + reset local history-revealed flag.
  useEffect(() => {
    if (!active) return;
    setHistoryLoaded(false);
    let stop = false;
    (async () => {
      await loadMessages(active.id);
      if (!stop) setTimeout(() => scrollBoxToBottom(scrollBox.current, false), 60);
    })();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [active?.id, endedAt]);

  // Auto-poll every 10 s so incoming messages surface without a manual refresh.
  // Also re-fetches the channel row so a clinic-wide End Chat cutoff pushed by
  // another clinician propagates automatically, and refreshes the per-channel
  // unread dots.
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      refreshChans().catch(() => {});
      loadMessages(active.id).catch(() => {});
      refreshUnread();
    }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [active?.id, endedAt]);

  const refreshAll = async () => {
    await refreshChans();
    if (active) {
      await loadMessages(active.id, { spin: true });
      setTimeout(() => scrollBoxToBottom(scrollBox.current, true), 60);
    }
    toast.success("Chat refreshed");
  };

  const endChat = async () => {
    if (!active) return;
    // Clinic-wide End Chat: server stamps `ended_at` on the channel so every
    // clinician's view clears on the next poll. Messages remain in the DB and
    // can be pulled back locally with "Chat history".
    try {
      const { data } = await api.post(`/team/channels/${active.id}/end-chat`);
      setActive((prev) => (prev ? { ...prev, ended_at: data.ended_at, ended_by_name: data.ended_by_name } : prev));
      setChannels((prev) => prev.map((c) => c.id === active.id ? { ...c, ended_at: data.ended_at, ended_by_name: data.ended_by_name } : c));
      setMessages([]);
      setHistoryLoaded(false);
      setText("");
      toast.success(`Chat ended in #${active.name} for everyone. Past messages stay in history.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end chat");
    }
  };

  const showHistory = async () => {
    if (!active) return;
    await loadMessages(active.id, { ignoreCutoff: true, spin: true });
    setTimeout(() => scrollBoxToBottom(scrollBox.current, true), 60);
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const body = text.trim();
    setText("");
    try {
      await api.post("/team/messages", { channel_id: active.id, body });
      await loadMessages(active.id);
      setTimeout(() => scrollBoxToBottom(scrollBox.current, true), 60);
    } catch { toast.error("Failed to send"); }
  };

  const cancelDraft = async () => {
    if (!active) return;
    // Reserved clinic-wide channels are protected server-side — bail early
    // with a friendly toast so the clinician isn't surprised by a 400.
    if (["general", "announcements"].includes((active.name || "").toLowerCase())) {
      toast.error(`#${active.name} is a protected channel and cannot be cancelled.`);
      return;
    }
    if (!window.confirm(`Cancel and remove #${active.name} from the channel list?\nAll messages inside it will be deleted. This cannot be undone.`)) return;
    try {
      await api.delete(`/team/channels/${active.id}`);
      toast.success(`#${active.name} cancelled and removed`);
      setChannels((prev) => prev.filter((c) => c.id !== active.id));
      setMessages([]);
      setText("");
      setActive(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel channel");
    }
  };

  const createChan = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team/channels", newChan);
      toast.success("Channel created");
      setNewChan({ name: "", description: "" });
      setShowNew(false);
      refreshChans();
    } catch { toast.error("Failed"); }
  };

  return (
    <DashLayout>
      <div className="mb-6">
        <div className="fl-label text-[#f47a20]">{t("pages.cTeam.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.cTeam.h1")}</h1>
      </div>

      <div className="grid md:grid-cols-4 gap-4 fl-card overflow-hidden" style={{ minHeight: 560 }}>
        <div className="border-r border-white/10 md:col-span-1 max-h-[560px] overflow-auto">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <div className="fl-label">Channels</div>
            <button onClick={() => setShowNew(!showNew)} className="text-[#f47a20] hover:text-white" data-testid="team-new-channel"><Plus size={14} /></button>
          </div>
          {showNew && (
            <form onSubmit={createChan} className="p-3 border-b border-white/10 space-y-2 bg-white/5">
              <input required placeholder="channel-name" className="fl-input text-xs" value={newChan.name} onChange={(e) => setNewChan({ ...newChan, name: e.target.value })} data-testid="team-chan-name" />
              <input placeholder="Description" className="fl-input text-xs" value={newChan.description} onChange={(e) => setNewChan({ ...newChan, description: e.target.value })} />
              <button className="fl-btn text-xs w-full justify-center">Create</button>
            </form>
          )}
          {channelsLoading && channels.length === 0 && (
            <div className="p-3 space-y-2" data-testid="team-channels-loading">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-2 flex items-center gap-2">
                  <Skeleton className="h-3 w-3 rounded" />
                  <div className="flex-1">
                    <Skeleton className="h-3 w-24 rounded-md" />
                    <Skeleton className="h-2 w-32 mt-2 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {channels.map((c) => {
            const isActive = active?.id === c.id;
            // Never surface an unread dot on the channel the user is currently
            // viewing — the messages have already been marked read on the
            // GET that fetched them.
            const unread = isActive ? 0 : Number(unreadByChan[c.id] || 0);
            return (
              <button key={c.id} onClick={() => setActive(c)}
                className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/5 flex items-center gap-2 ${isActive ? "bg-white/5 border-l-2 border-l-[#f47a20]" : ""}`}
                data-testid={`team-chan-${c.name}`}>
                <Hash size={12} className="text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm flex items-center gap-2 ${unread > 0 ? "font-semibold text-white" : ""}`}>
                    <span className="truncate">{c.name}</span>
                    {unread > 0 && (
                      <span
                        data-testid={`team-chan-dot-${c.name}`}
                        aria-label={`${unread} unread`}
                        className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex-shrink-0"
                        style={{ background: "#f47a20" }}
                      >
                        {unread > 9 ? "9+" : unread}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">{c.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="md:col-span-3 flex flex-col max-h-[560px]">
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Hash size={16} className="text-[#f47a20]" />
                <div className="fl-heading text-lg truncate">{active?.name || "Select a channel"}</div>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{active?.description}</div>
            </div>
            {active && (
              <button
                onClick={refreshAll}
                className="fl-btn-ghost text-xs"
                title="Reload channel list and messages"
                data-testid="team-refresh"
              >
                <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
              </button>
            )}
            {active && endedAt && !historyLoaded && (
              <button
                onClick={showHistory}
                className="fl-btn-ghost text-xs"
                title="Reveal messages posted before you ended the chat"
                data-testid="team-show-history"
              >
                <History size={13} /> Chat history
              </button>
            )}
            {active && messages.length > 0 && !endedAt && (
              <button
                onClick={endChat}
                className="fl-btn-ghost text-xs"
                title="Clear the visible chat — history stays saved"
                data-testid="team-end-chat"
              >
                <MessageSquareOff size={13} /> End chat
              </button>
            )}
            {active && (
              <button
                onClick={cancelDraft}
                className="fl-btn-ghost text-xs text-red-400 hover:text-red-300"
                title="Cancel this channel — deletes it and all messages inside"
                data-testid="team-cancel-new"
              >
                <X size={12} /> Cancel
              </button>
            )}
          </div>
          <div ref={scrollBox} className="flex-1 overflow-auto p-4 space-y-3">
            {messages.length === 0 && !endedAt && (
              <div className="h-full flex items-center justify-center text-center text-sm py-8" style={{ color: "var(--fl-muted-2)" }}>
                No messages yet in <b>#{active?.name || "…"}</b>. Say hi to start the conversation.
              </div>
            )}
            {messages.length === 0 && endedAt && (
              <div className="h-full flex items-center justify-center text-center text-sm py-8" style={{ color: "var(--fl-muted-2)" }}>
                Chat cleared for <b>#{active?.name || "…"}</b>. Send a new message to start a fresh conversation,<br />
                or tap <b>Chat history</b> to review what was said earlier.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className="flex gap-3" data-testid={`team-msg-${m.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#f47a20]/20 text-[#f47a20] flex items-center justify-center text-xs font-bold shrink-0">
                  {m.author_name?.split(" ").map(w => w[0]).join("").slice(0, 2)}
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <div className="fl-heading text-sm">{m.author_name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">{m.author_role}</div>
                    <div className="text-[10px] text-zinc-500">{new Date(m.created_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>
                  </div>
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap mt-1">{m.body}</div>
                </div>
              </div>
            ))}
            <div ref={bottom} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input className="fl-input" placeholder={`Message #${active?.name || "…"}`} value={text}
              onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="team-input" />
            <button className="fl-btn" onClick={send} data-testid="team-send"><Send size={14} /></button>
          </div>
        </div>
      </div>
    </DashLayout>
  );
}
